import { useState } from "preact/hooks";
import { batch } from "@preact/signals";
import type { ProgramWork } from "../types";
import { workText } from "../utils/workTitle";
import { selectedComposerId, setPieceFilter, viewMode } from "../state/store";

interface ProgramListProps {
  program: ProgramWork[];
  maxVisible?: number;
}

const PART_LABELS: Record<string, string> = { overture: "Overture", suite: "Suite", excerpt: "Excerpts" };

/** The facet key api/pieces.py piece_key() would mint for a resolved entry — `w<work_id>`
 *  plus `~<part_kind>` when the entry is a part. Only resolved entries get a link: an
 *  unresolved display group is keyed on a hash the card can't recompute. */
function pieceKeyOf(p: ProgramWork): string | null {
  if (!p.work_id || !p.work_canonical) return null;
  const part = p.part_kind && p.part_kind !== "complete" ? `~${p.part_kind}` : "";
  return `w${p.work_id}${part}`;
}

function gotoExplore() {
  viewMode.value = "explore";
  requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
}

/** Composer — work list shared by <ConcertCard> and <HistoricalConcertCard>: same shape
 *  (`ProgramWork[]`), same "colon not em dash" convention, same expand-for-more affordance.
 *  Keeping this one component is what makes Archive mode read as the same product rather
 *  than a bolt-on with its own program-rendering rules.
 *
 *  Entries the catalogue has resolved — a matched composer AND a matched work — render as
 *  links into Explore (the piece across every concert, or the composer) — the tap is the
 *  reader's way from one concert to the whole repertoire. Nothing decorates them at rest:
 *  an accent dot before the title (2026-09-06) read as noise, and a dotted underline
 *  (2026-09-06c) hazed the text grey. A resolved entry is simply set at full text
 *  strength, and the underline shows up on hover/focus. Unresolved entries stay plain
 *  text, exactly as the presenter printed them, one notch softer. */
export function ProgramList({ program, maxVisible = 8 }: ProgramListProps) {
  const [expanded, setExpanded] = useState(false);
  const hasMore = program.length > maxVisible;
  const visible = expanded ? program : program.slice(0, maxVisible);

  if (program.length === 0) return null;

  return (
    <ul class="concert-program">
      {visible.map((p, i) => {
        const composer = p.composers[0];
        const composerLinked = !p.unspecified && p.composers.length === 1 && composer?.id != null;
        const pieceKey = composerLinked ? pieceKeyOf(p) : null;
        const text = workText(p);
        return (
          <li key={i} class={pieceKey ? "is-resolved" : undefined}>
            {/* A demoted program block is a composer credit, not a piece — the scraped
                "title" was the program name, a works-by list, or a lecture description. */}
            {p.unspecified && p.composers.length > 0 ? (
              <span class="program-composer">
                Works by {p.composers.map((composer) => composer.name).join(", ")}
              </span>
            ) : null}
            {!p.unspecified && p.composers.length > 0 && (
              composerLinked ? (
                <button
                  type="button"
                  class="program-composer program-link"
                  title={`Explore ${composer!.name}`}
                  onClick={() => {
                    batch(() => {
                      setPieceFilter(null);
                      selectedComposerId.value = composer!.id!;
                    });
                    gotoExplore();
                  }}
                >
                  {composer!.name}
                </button>
              ) : (
                <span class="program-composer">
                  {p.composers.map((composer) => composer.name).join(", ")}
                </span>
              )
            )}
            {/* Colon, not an em dash: "Beethoven: Symphony No. 5" is how concert programs
                and record sleeves have always set this, and it costs far less horizontal
                space on a phone than " — ". */}
            {!p.unspecified && text && p.composers.length > 0 && <span class="program-sep">: </span>}
            {!p.unspecified && text && (
              pieceKey ? (
                <button
                  type="button"
                  class="program-work program-link program-work-link"
                  title="In the works catalogue — see every concert with this piece"
                  onClick={() => {
                    const part = p.part_kind && p.part_kind !== "complete" ? PART_LABELS[p.part_kind] : null;
                    // The composer rides along: the piece profile implies it, and
                    // removing the piece chip steps back to the composer's profile.
                    batch(() => {
                      selectedComposerId.value = composer!.id ?? null;
                      setPieceFilter({
                        key: pieceKey,
                        label: part ? `${p.work_canonical} (${part})` : p.work_canonical!,
                        composer: composer!.name,
                        composer_id: composer!.id ?? null,
                        part_kind: p.part_kind ?? null,
                        count: 0,
                      });
                    });
                    gotoExplore();
                  }}
                >
                  {text}
                </button>
              ) : (
                <span class="program-work">{text}</span>
              )
            )}
            {p.arrangers.length > 0 && (
              <span class="program-arranger">
                {" "}
                (arr. {p.arrangers.map((arranger) => arranger.name).join(", ")})
              </span>
            )}
          </li>
        );
      })}
      {hasMore && !expanded && (
        <li class="concert-expand-row">
          <button class="concert-expand" onClick={() => setExpanded(true)} type="button">
            + {program.length - maxVisible} more {program.length - maxVisible === 1 ? "work" : "works"}
          </button>
        </li>
      )}
    </ul>
  );
}
